import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { View, StyleSheet, Alert, KeyboardAvoidingView, Platform } from 'react-native';
import { GiftedChat, IMessage, Send, InputToolbar as GiftedInputToolbar, Composer, Bubble, MessageText, InputToolbarProps } from 'react-native-gifted-chat';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useContextualMessaging } from '../utils/useContextualMessaging';
import { Text, Button } from 'tamagui';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import { useSelector, useDispatch } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { FLIP_MESSAGE_MODAL } from '@reduxLocal/actions/types';
import SocketManager from '@utils/socket';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import ParsedTextWithReferences from '@navigation/screens/MidLevel/EasyModeScreen/Components/ParsedTextWithReferences';
import { useNavigation } from '@react-navigation/native';
import type { ParsedReference } from '@navigation/screens/MidLevel/EasyModeScreen/utils/chatUtils';
import InputToolbar from './InputToolbar';
import ErrorBoundary from '@utils/ErrorBoundary';
import { toggleChatbotRouting } from '@api/whatsappApi';
import { useQueryClient } from '@tanstack/react-query';

interface ContextualChatProps {
  conversationId: string;
  recipientId: string;
  recipientName?: string;
  businessPhoneNumberId?: string;
  contactInfo?: any;
  contactName?: string;
  contactProfilePicture?: string;
  participantPhone?: string;
  initialMessages?: IMessage[];
  onMessagesChange?: (messages: IMessage[]) => void;
}

export const ContextualChat: React.FC<ContextualChatProps> = ({
  conversationId,
  recipientId,
  recipientName,
  businessPhoneNumberId,
  contactInfo,
  contactName,
  contactProfilePicture,
  participantPhone,
  initialMessages = [],
  onMessagesChange
}) => {
  const {
    sendMessage,
    getMessagesForConversation,
    markAsRead,
    isWhatsAppContext
  } = useContextualMessaging();

  // Get conversationId from Redux state if not provided as prop
  const reduxConversation = useSelector((state: DefaultRootState) => state.modals.messageModal.content);
  const actualConversationId = conversationId || reduxConversation?.id || reduxConversation?.conversationId;

  // Get selectedConversation for InputToolbar targetUser prop (same as ChatContainer)
  const selectedConversation = reduxConversation || {
    id: conversationId,
    participantId: recipientId,
    conversationId: actualConversationId,
  };

  const theme = themeSelector();
  const [layoutWidth, setLayoutWidth] = useState<number>(0);
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  // Get authenticated user for GiftedChat (only for alignment purposes)
  const authenticatedUser = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
  const authenticatedUserId = authenticatedUser?._id || authenticatedUser?.id || authenticatedUser?.userId || 'current-user';

  const [messages, setMessages] = useState<IMessage[]>(initialMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);

  console.log('📱 ContextualChat - Using conversationId:', {
    propConversationId: conversationId,
    reduxConversationId: reduxConversation?.id || reduxConversation?.conversationId,
    actualConversationId,
    reduxConversationKeys: reduxConversation ? Object.keys(reduxConversation) : 'null',
    reduxConversationIdField: reduxConversation?.id,
    reduxConversationConversationIdField: reduxConversation?.conversationId,
    reduxConversation: reduxConversation
  });

  // Track if we've already loaded messages for this conversation to prevent unnecessary refetches
  const [loadedConversationId, setLoadedConversationId] = useState<string | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Get chatbot routing state from conversation data (from Redux or API)
  // Use local state for optimistic updates
  const [localChatbotRoutingEnabled, setLocalChatbotRoutingEnabled] = useState<boolean | null>(null);
  const isChatbotRoutingEnabled = localChatbotRoutingEnabled !== null
    ? localChatbotRoutingEnabled
    : (reduxConversation?.chatbotRoutingEnabled || false);

  // Initialize local state from Redux when conversation changes
  useEffect(() => {
    if (reduxConversation?.chatbotRoutingEnabled !== undefined) {
      setLocalChatbotRoutingEnabled(reduxConversation.chatbotRoutingEnabled);
    }
  }, [reduxConversation?.chatbotRoutingEnabled, actualConversationId]);

  // Handler for chatbot routing switch toggle - calls backend API
  const handleChatbotRoutingChange = useCallback(async (enabled: boolean) => {
    if (actualConversationId) {
      // Optimistically update local state immediately
      setLocalChatbotRoutingEnabled(enabled);

      // Also update Redux state optimistically
      if (reduxConversation) {
        dispatch({
          type: FLIP_MESSAGE_MODAL,
          payload: {
            ...reduxConversation,
            chatbotRoutingEnabled: enabled
          }
        });
      }

      try {
        await toggleChatbotRouting(actualConversationId, enabled);
        console.log('🤖 ContextualChat - Chatbot routing', enabled ? 'enabled' : 'disabled', 'for conversation:', actualConversationId);

        // Invalidate conversation queries to refetch updated data
        queryClient.invalidateQueries({ queryKey: ['all-users'] });
        queryClient.invalidateQueries({
          queryKey: ['whatsapp-conversations', businessPhoneNumberId]
        });
      } catch (error) {
        console.error('❌ ContextualChat - Error toggling chatbot routing:', error);
        // Revert optimistic update on error
        setLocalChatbotRoutingEnabled(!enabled);
        if (reduxConversation) {
          dispatch({
            type: FLIP_MESSAGE_MODAL,
            payload: {
              ...reduxConversation,
              chatbotRoutingEnabled: !enabled
            }
          });
        }
        Alert.alert('Error', 'Failed to update chatbot routing setting. Please try again.');
      }
    }
  }, [actualConversationId, queryClient, businessPhoneNumberId, reduxConversation, dispatch]);

  // Set messages when initialMessages prop changes or fetch messages if empty
  useEffect(() => {
    console.log('📱 ContextualChat - Effect triggered:', {
      length: initialMessages.length,
      isWhatsAppContext,
      conversationId,
      actualConversationId,
      loadedConversationId,
      isInitialLoad,
      reduxConversationId: reduxConversation?.id || reduxConversation?.conversationId,
      firstMessage: initialMessages[0]
    });
    
    // Only fetch if conversationId changed or it's the initial load
    const shouldFetch = isInitialLoad || (actualConversationId && actualConversationId !== loadedConversationId);

    if (isWhatsAppContext && actualConversationId && shouldFetch) {
      // Fetch messages when conversationId changes or on initial load
      console.log('📱 ContextualChat - Fetching messages for conversation:', {
        actualConversationId,
        actualConversationIdType: typeof actualConversationId,
        reduxConversationId: reduxConversation?.id || reduxConversation?.conversationId,
        reduxConversationKeys: reduxConversation ? Object.keys(reduxConversation) : 'null'
      });
      const loadMessages = async () => {
        try {
          setIsLoading(true);
          const loadedMessages = await getMessagesForConversation(actualConversationId, 1, 50);
          console.log('📱 ContextualChat - Loaded messages:', {
            count: loadedMessages.length,
            conversationIdUsed: actualConversationId,
            firstMessage: loadedMessages[0] ? {
              _id: loadedMessages[0]._id,
              text: loadedMessages[0].text?.substring(0, 30),
              user: loadedMessages[0].user
            } : null
          });

          // Debug: Log user ID matching for alignment
          if (__DEV__) {
            console.log('📱 ContextualChat - Message alignment check:', {
              currentUserId: currentUser._id,
              messages: loadedMessages.map(msg => ({
                _id: msg._id,
                text: msg.text?.substring(0, 20),
                userId: msg.user?._id,
                userName: msg.user?.name,
                willAlignRight: msg.user?._id === currentUser._id,
                matches: msg.user?._id === currentUser._id
              }))
            });
          }

          setMessages(loadedMessages);
          onMessagesChange?.(loadedMessages);
          setLoadedConversationId(actualConversationId);
          setIsInitialLoad(false);
        } catch (error) {
          console.error('📱 ContextualChat - Error loading messages:', error);
          // Fallback to initialMessages if fetch fails
          if (initialMessages.length > 0) {
            setMessages(initialMessages);
            onMessagesChange?.(initialMessages);
          } else {
            setMessages([]);
          }
          setIsInitialLoad(false);
        } finally {
          setIsLoading(false);
        }
      };
      
      loadMessages();
    } else if (initialMessages.length > 0 && isInitialLoad) {
      // Use initialMessages only on first load if available
      console.log('📱 ContextualChat - Setting messages from initialMessages:', initialMessages.length);
      setMessages(initialMessages);
      onMessagesChange?.(initialMessages);
      setIsInitialLoad(false);
    } else if (!actualConversationId && !isInitialLoad) {
      console.log('📱 ContextualChat - No conversationId, setting empty array');
      setMessages([]);
    }
  }, [isWhatsAppContext, actualConversationId]); // Only depend on conversationId, not initialMessages

  // Socket listeners for real-time message updates
  useEffect(() => {
    if (!isWhatsAppContext || !actualConversationId) return;

    const socketManager = new SocketManager();
    const socket = socketManager.getSocket();
    
    if (!socket) return;

    console.log('📱 ContextualChat - Setting up socket listeners for conversation:', actualConversationId);

    const handleWhatsAppMessage = (messageData: any) => {
      // Normalize conversation IDs to strings for comparison
      const messageConversationId = messageData.conversationId
        ? String(messageData.conversationId)
        : null;
      const normalizedActualConversationId = actualConversationId
        ? String(actualConversationId)
        : null;

      console.log('📱 ContextualChat - Received WhatsApp message:', {
        rawConversationId: messageData.conversationId,
        conversationIdType: typeof messageData.conversationId,
        conversationIdValue: messageData.conversationId,
        messageConversationId: messageConversationId,
        rawActualConversationId: actualConversationId,
        actualConversationIdType: typeof actualConversationId,
        actualConversationIdValue: actualConversationId,
        normalizedActualConversationId: normalizedActualConversationId,
        messageId: messageData.messageId,
        direction: messageData.direction,
        from: messageData.from,
        to: messageData.to,
        matches: messageConversationId === normalizedActualConversationId
      });

      // Only process messages for the current conversation
      if (messageConversationId !== normalizedActualConversationId) {
        console.log('📱 ContextualChat - Message not for current conversation, ignoring:', {
          messageConversationId,
          normalizedActualConversationId,
          rawMessageId: messageData.conversationId,
          rawActualId: actualConversationId,
          messageId: messageData.messageId,
          direction: messageData.direction,
          comparison: {
            strict: messageData.conversationId === actualConversationId,
            normalized: messageConversationId === normalizedActualConversationId,
            stringComparison: String(messageData.conversationId) === String(actualConversationId)
          }
        });
        return;
      }

      console.log('✅ ContextualChat - Message matches current conversation, processing:', {
        messageConversationId,
        normalizedActualConversationId,
        messageId: messageData.messageId
      });

      // Process both inbound and outbound messages
      // Inbound: from contact to business
      // Outbound: from business to contact

      // Helper function to extract text content from message
      const extractMessageText = (msg: any): string => {
        // Handle template messages
        if (msg.type === 'template') {
          const templateName = msg.content?.template?.name || msg.content?.name || 'Template';
          const templateLanguage = msg.content?.template?.language || msg.content?.language || 'en';

          // Extract parameter values from template components
          // Parameters can be stored as components array or directly as parameters
          const components = msg.content?.template?.components || msg.content?.template?.parameters || [];
          const parameterTexts: string[] = [];

          components.forEach((component: any) => {
            // Component can have parameters array (WhatsApp API format)
            if (component.parameters && Array.isArray(component.parameters)) {
              component.parameters.forEach((param: any) => {
                if (param.text) {
                  parameterTexts.push(param.text);
                }
              });
            }
            // Or component can be a direct parameter object
            else if (component.text) {
              parameterTexts.push(component.text);
            }
          });

          // Format template message display
          if (parameterTexts.length > 0) {
            return `📝 ${templateName} (${templateLanguage})\n${parameterTexts.join('\n')}`;
          }
          return `📝 ${templateName} (${templateLanguage})`;
        }

        // Handle other message types
        // Ensure we always return a string, not an array
        const textContent = msg.text || msg.content?.body || msg.content?.text || msg.media?.caption || '';

        // If textContent is an array, join it (shouldn't happen, but safety check)
        if (Array.isArray(textContent)) {
          console.warn('⚠️ Socket message text is an array, joining:', textContent);
          return textContent.join('');
        }

        // Ensure it's a string
        return String(textContent);
      };

      // Transform the message to GiftedChat format
      // Ensure user._id is always a string (required by GiftedChat)
      // For outbound messages, use authenticated user ID to match currentUser._id (right alignment)
      // For inbound messages, use phone number so it doesn't match (left alignment)
      const userId = messageData.direction === 'outbound'
        ? String(authenticatedUserId)  // Must match currentUser._id for right alignment
        : (messageData.from ? String(messageData.from) : 'unknown');

      // Ensure user.name is always a string
      // For inbound messages, prioritize contactInfo from props (Redux state), then from message data
      const userName = messageData.direction === 'outbound'
        ? (authenticatedUser?.profile_information?.name || authenticatedUser?.name || 'You')
        : (contactInfo?.name || contactInfo?.profileName || contactName || messageData.contactName || messageData.contactInfo?.name || messageData.contactInfo?.profileName || (messageData.from ? String(messageData.from) : 'Unknown Contact'));

      // Extract text and ensure it's a string (not an array)
      let messageText = extractMessageText(messageData);

      // CRITICAL: Safety check - if text is an array, join it immediately
      if (Array.isArray(messageText)) {
        console.warn('⚠️ Socket message text is an array, joining:', {
          messageId: messageData.messageId || messageData._id,
          textArray: messageText,
          joined: messageText.join('')
        });
        messageText = messageText.join('');
      }

      // Note: Chatbot routing is now handled by the backend
      // The backend will check chatbotRoutingEnabled and send AI responses automatically
      // The AI response will come through the same socket as a normal outbound message

      // Additional check: if messageText is still not a string (e.g., object), convert it
      if (typeof messageText !== 'string') {
        console.warn('⚠️ Socket message text is not a string, converting:', {
          messageId: messageData.messageId || messageData._id,
          type: typeof messageText,
          value: messageText
        });
        messageText = String(messageText);
      }

      // Final safety: ensure it's always a string
      messageText = String(messageText || '');

      const newMessage: IMessage = {
        _id: messageData.messageId || messageData._id || String(messageData._id),
        text: messageText,
        createdAt: new Date(messageData.timestamp),
        user: {
          _id: userId, // Always a string, required by GiftedChat
          name: userName, // Always a string
          // Prioritize contactProfilePicture from props (Redux state), then from message data
          avatar: contactProfilePicture || messageData.contactProfilePicture || messageData.contactInfo?.profilePicture || undefined,
        },
      };

      // Debug logging for alignment
      if (__DEV__) {
        console.log('📱 Socket message transformation:', {
          messageId: newMessage._id,
          direction: messageData.direction,
          userId: newMessage.user._id,
          currentUserId: currentUser._id,
          willAlignRight: newMessage.user._id === currentUser._id,
          matches: newMessage.user._id === currentUser._id
        });
      }

      console.log('📱 ContextualChat - Adding new message to chat:', newMessage);

      // Check if message already exists to avoid duplicates
      setMessages(previousMessages => {
        const messageExists = previousMessages.some(msg => msg._id === newMessage._id);
        if (messageExists) {
          console.log('📱 ContextualChat - Message already exists, skipping:', newMessage._id);
          return previousMessages;
        }

        const updatedMessages = GiftedChat.append(previousMessages, [newMessage]);
        console.log('📱 ContextualChat - Messages updated, total count:', updatedMessages.length);
        onMessagesChange?.(updatedMessages);
        return updatedMessages;
      });
    };

    const handleWhatsAppStatusUpdate = (statusData: any) => {
      console.log('📱 ContextualChat - Received WhatsApp status update:', statusData);
      
      // Update message status if it's for the current conversation
      if (statusData.conversationId === actualConversationId) {
        setMessages(previousMessages => {
          return previousMessages.map(msg => {
            if (msg._id === statusData.messageId) {
              // Update the message status (you might want to add a status field to IMessage)
              return { ...msg, status: statusData.status };
            }
            return msg;
          });
        });
      }
    };

    // Listen for WhatsApp events
    socket.on('whatsapp:message', handleWhatsAppMessage);
    socket.on('whatsapp:status', handleWhatsAppStatusUpdate);

    return () => {
      console.log('📱 ContextualChat - Cleaning up socket listeners');
      socket.off('whatsapp:message', handleWhatsAppMessage);
      socket.off('whatsapp:status', handleWhatsAppStatusUpdate);
    };
  }, [isWhatsAppContext, actualConversationId, onMessagesChange, authenticatedUserId, authenticatedUser]);

  // Current user for GiftedChat
  // IMPORTANT: user._id must match exactly for messages to align correctly
  // Messages with user._id === currentUser._id appear on the right (sent by current user)
  // Messages with user._id !== currentUser._id appear on the left (received from contact)
  // For WhatsApp, use authenticated user ID (not 'business') for GiftedChat alignment purposes only
  const currentUser = useMemo(() => {
    const user = {
      _id: isWhatsAppContext ? String(authenticatedUserId) : 'current-user',
      name: isWhatsAppContext ? (authenticatedUser?.profile_information?.name || authenticatedUser?.name || 'You') : 'You',
    };

    if (__DEV__) {
      console.log('📱 ContextualChat - Current user set:', {
        ...user,
        authenticatedUserId,
        isWhatsAppContext
      });
    }

    return user;
  }, [isWhatsAppContext, authenticatedUserId, authenticatedUser]);

  // Send text message
  const onSend = useCallback(async (newMessages: IMessage[] = []) => {
    const [message] = newMessages;
    if (!message?.text?.trim()) return;

    try {
      setIsLoading(true);
      
      console.log('📱 WhatsApp ContextualChat - Sending message:', {
        text: message.text,
        recipientId,
        conversationId,
        businessPhoneNumberId,
        isWhatsAppContext,
        recipientIdType: typeof recipientId,
        recipientIdValue: recipientId
      });
      
      // Update UI immediately for better UX
      setMessages(previousMessages => GiftedChat.append(previousMessages, newMessages));
      onMessagesChange?.(GiftedChat.append(messages, newMessages));

      // Send the message
      const response = await sendMessage({
        to: recipientId,
        message: message.text,
        conversationId,
        messageType: 'text',
        businessPhoneNumberId
      });

      console.log(`✅ Message sent successfully ${isWhatsAppContext ? 'via WhatsApp' : 'internally'}:`, response);

      // Wait a bit before refetching to ensure message is saved to database
      // The socket event should handle real-time updates, but we refetch as backup
      if (isWhatsAppContext && actualConversationId) {
        // Use setTimeout to delay refetch slightly, allowing database save to complete
        setTimeout(async () => {
          try {
            console.log('📱 ContextualChat - Refetching messages after send (delayed)');
            const refreshedMessages = await getMessagesForConversation(actualConversationId, 1, 50);
            console.log('📱 ContextualChat - Refreshed messages count:', refreshedMessages.length);

            // Merge with existing messages to avoid losing optimistic updates
            setMessages(previousMessages => {
              // Create a map of existing message IDs to avoid duplicates
              const existingIds = new Set(previousMessages.map(m => m._id));
              const newMessages = refreshedMessages
                .filter(m => !existingIds.has(m._id))
                .map((msg: IMessage) => {
                  // Messages from getMessagesForConversation should already have correct user IDs
                  // based on direction (outbound = authenticatedUserId, inbound = phone number)
                  // We just need to ensure they're strings and handle legacy 'business' IDs
                  const userId = msg.user?._id;
                  const userName = msg.user?.name;

                  // Ensure user._id is always a string
                  let correctUserId = userId
                    ? String(userId)  // Ensure it's a string
                    : currentUser._id; // Fallback to currentUser if missing

                  // Convert legacy 'business' ID to authenticated user ID
                  if (correctUserId === 'business') {
                    correctUserId = String(authenticatedUserId);
                  }

                  // IMPORTANT: If userId already matches authenticatedUserId, keep it (it's outbound)
                  // If it doesn't match, keep it as is (it's inbound with phone number)
                  // Don't try to "correct" it - trust the transformation from getMessagesForConversation

                  const correctUserName = userName
                    ? String(userName)
                    : (correctUserId === currentUser._id ? currentUser.name : 'Unknown Contact');

                  // Log for debugging
                  if (__DEV__) {
                    console.log('📱 ContextualChat - Merging message with user ID:', {
                      messageId: msg._id,
                      originalUserId: userId,
                      correctUserId,
                      authenticatedUserId,
                      currentUserId: currentUser._id,
                      willAlignRight: correctUserId === currentUser._id,
                      text: msg.text?.substring(0, 30)
                    });
                  }

                  return {
                    ...msg,
                    user: {
                      _id: correctUserId,
                      name: correctUserName,
                      avatar: msg.user?.avatar,
                    }
                  };
                });

              // Combine and sort
              const combined = [...previousMessages, ...newMessages].sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
              );

              console.log('📱 ContextualChat - Merged messages:', {
                previous: previousMessages.length,
                new: newMessages.length,
                combined: combined.length,
                userIds: combined.map(m => ({
                  _id: m._id,
                  userId: m.user?._id,
                  matchesCurrentUser: m.user?._id === currentUser._id
                }))
              });

              return combined;
            });
          } catch (refetchError) {
            console.error('📱 ContextualChat - Error refetching messages after send:', refetchError);
            // Don't fail the send operation if refetch fails
          }
        }, 1000); // Wait 1 second for database save
      }
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      Alert.alert(
        'Message Failed',
        `Failed to send message ${isWhatsAppContext ? 'via WhatsApp' : ''}. Please try again.`
      );
      
      // Remove the failed message from UI
      setMessages(previousMessages => 
        previousMessages.filter(msg => msg._id !== message._id)
      );
    } finally {
      setIsLoading(false);
    }
  }, [sendMessage, recipientId, conversationId, onMessagesChange, isWhatsAppContext, businessPhoneNumberId, actualConversationId, getMessagesForConversation, currentUser, authenticatedUserId, authenticatedUser]); // Include authenticated user for alignment

  // Send media message
  const sendMediaMessage = useCallback(async (mediaUri: string, mediaType: 'image' | 'video' | 'audio' | 'document', caption?: string) => {
    try {
      setIsLoading(true);

      const tempMessage: IMessage = {
        _id: Math.random().toString(),
        text: caption || '',
        createdAt: new Date(),
        user: currentUser,
        [mediaType === 'image' ? 'image' : mediaType === 'video' ? 'video' : 'file']: mediaUri,
      };

      // Update UI immediately
      setMessages(previousMessages => GiftedChat.append(previousMessages, [tempMessage]));

      await sendMessage({
        to: recipientId,
        message: caption || '',
        conversationId,
        messageType: 'media',
        mediaUrl: mediaUri,
        mediaType,
        caption,
        businessPhoneNumberId
      });

      console.log(`${mediaType} message sent successfully`);
    } catch (error) {
      console.error('Failed to send media message:', error);
      Alert.alert('Failed to Send', `Failed to send ${mediaType}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  }, [sendMessage, recipientId, conversationId, currentUser]);

  // Pick and send image
  const pickImage = useCallback(async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission Required', 'Please grant camera roll permissions to send images.');
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
    });

    if (!result.canceled && result.assets[0]) {
      await sendMediaMessage(result.assets[0].uri, 'image');
    }
  }, [sendMediaMessage]);

  // Pick and send document
  const pickDocument = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        await sendMediaMessage(result.assets[0].uri, 'document');
      }
    } catch (error) {
      console.error('Document picker error:', error);
    }
  }, [sendMediaMessage]);

  // Load earlier messages
  const loadEarlier = useCallback(async () => {
    if (isLoadingEarlier) return;

    try {
      setIsLoadingEarlier(true);
      const currentPage = Math.ceil(messages.length / 50) + 1;
      const earlierMessages = await getMessagesForConversation(conversationId, currentPage, 50);
      
      if (earlierMessages.length > 0) {
        // Ensure messages are in the correct IMessage format
        const formattedMessages = earlierMessages.map(msg => {
          // Ensure user._id is always a string (required by GiftedChat)
          const userId = msg.user?._id ? String(msg.user._id) : 'unknown';
          const userName = msg.user?.name ? String(msg.user.name) : 'Unknown';

          return {
            _id: msg._id ? String(msg._id) : Math.random().toString(),
            text: msg.text || '',
            createdAt: msg.createdAt || new Date(),
            user: {
              _id: userId, // Always a string, required by GiftedChat
              name: userName, // Always a string
              avatar: msg.user?.avatar || undefined,
            },
            ...msg // Spread any additional properties
          };
        });
        setMessages(previousMessages => GiftedChat.prepend(previousMessages, formattedMessages));
      }
    } catch (error) {
      console.error('Failed to load earlier messages:', error);
    } finally {
      setIsLoadingEarlier(false);
    }
  }, [getMessagesForConversation, conversationId, isLoadingEarlier]); // Removed messages.length to prevent infinite re-renders

  // Mark conversation as read when component mounts or conversation changes
  React.useEffect(() => {
    markAsRead(conversationId);
  }, [conversationId]); // Removed markAsRead to prevent infinite re-renders

  // Custom send button
  const renderSend = useCallback((props: any) => (
    <Send {...props}>
      <View style={styles.sendButton}>
        <MaterialCommunityIcons 
          name="send" 
          size={24} 
          color={isWhatsAppContext ? "#25D366" : "#007AFF"} 
        />
      </View>
    </Send>
  ), [isWhatsAppContext]);

  // Custom input toolbar with media buttons
  // Custom input toolbar - use same vertical layout as default messaging service for WhatsApp
  const renderInputToolbar = useCallback((props: InputToolbarProps<any>) => {
    if (isWhatsAppContext) {
      // Use the same InputToolbar component and props as ChatContainer (default messaging service)
      return (
        <ErrorBoundary>
          <InputToolbar
            {...(props as any)}
            targetUser={selectedConversation}
            maxHeight={300}
            removeTools={false}
            containerStyle={{ maxHeight: "27%", paddingBottom: 0, paddingTop: 0 }}
            placeHolder="Enter your message here..."
            multiline
            inverse
            removeAttach
            forceColumn
            staticPress
            showChatbotSwitch={true}
            enableChatbotRouting={isChatbotRoutingEnabled}
            onChatbotRoutingChange={handleChatbotRoutingChange}
          />
        </ErrorBoundary>
      );
    } else {
      // Default behavior for non-WhatsApp context
      return (
        <GiftedInputToolbar
          {...props}
          containerStyle={[
            styles.inputToolbar,
            {
              borderTopColor: '#007AFF',
            }
          ]}
        />
      );
    }
  }, [isWhatsAppContext, selectedConversation, isChatbotRoutingEnabled, handleChatbotRoutingChange]);

  // Custom composer
  const renderComposer = useCallback((props: any) => (
    <Composer
      {...props}
      textInputStyle={[
        styles.composer,
        {
          borderColor: isWhatsAppContext ? '#25D366' : '#007AFF',
          backgroundColor: '#FFFFFF', // White background for input field
          color: '#000000' // Black text color
        }
      ]}
      placeholder={`Type a message${isWhatsAppContext ? ' (WhatsApp)' : ''}...`}
      placeholderTextColor="#999999"
    />
  ), [isWhatsAppContext]);

  // Layout handler to get container width (same as ChatContainer)
  const handleLayout = useCallback((event: any) => {
    const { width } = event.nativeEvent.layout;
    setLayoutWidth(width);
  }, []);

  // Custom renderer for message bubbles - same styling as default messaging service
  const renderBubble = useCallback((props: any) => (
    <Bubble
      {...props}
      wrapperStyle={{
        right: {
          maxWidth: layoutWidth > 0 ? layoutWidth * 0.8 : '80%',
          paddingHorizontal: 12,
          paddingVertical: 8,
          ...props.wrapperStyle?.right,
        },
        left: {
          maxWidth: layoutWidth > 0 ? layoutWidth * 0.8 : '80%',
          paddingHorizontal: 12,
          paddingVertical: 8,
          ...props.wrapperStyle?.left,
        },
      }}
    />
  ), [layoutWidth]);

  // Handler for reference button clicks
  const handleReferencePress = useCallback((reference: ParsedReference) => {
    try {
      switch (reference.type) {
        case 'data':
          (navigation as any).navigate('DataBrowser', {
            requestId: reference.id,
            title: reference.title,
          });
          break;
        case 'project':
          if (Platform.OS === 'web') {
            (navigation as any).replace('IndividualProjectPage', {
              projectId: reference.id,
              title: reference.title,
            });
          } else {
            (navigation as any).navigate('IndividualProjectPage', {
              project: {
                title: reference.title,
                _id: reference.id,
              },
            });
          }
          break;
        case 'item':
          // Navigate to generic data item screen
          (navigation as any).navigate('GenericDataItemScreen', {
            dataId: reference.id,
            contentId: reference.id,
          });
          break;
        case 'calendar':
          // Navigate to calendar screen - you may need to adjust this based on your calendar route
          (navigation as any).navigate('Calendar', {
            eventId: reference.id,
          });
          break;
        default:
          console.warn('Unknown reference type:', reference.type);
      }
    } catch (error) {
      console.error('Error navigating to reference:', error);
    }
  }, [navigation]);

  // Custom renderer for message text to ensure wrapping and parse references
  const renderMessageText = useCallback((props: any) => {
    let messageText = props.currentMessage?.text || '';

    // CRITICAL: Ensure messageText is always a string, not an array
    // If it's an array, join it immediately
    if (Array.isArray(messageText)) {
      console.warn('⚠️ renderMessageText - text is an array, joining:', {
        messageId: props.currentMessage?._id,
        textArray: messageText,
        joined: messageText.join('')
      });
      messageText = messageText.join('');
    }

    // Ensure it's a string
    messageText = String(messageText);

    const isRight = props.position === 'right';
    const textColor = isRight
      ? (props.textStyle?.right?.color || '#FFFFFF')
      : (props.textStyle?.left?.color || '#000000');

    return (
      <View style={{ flex: 1, paddingHorizontal: 10, paddingVertical: 6, maxWidth: '100%' }}>
        <ParsedTextWithReferences
          text={messageText}
          textStyle={{
            ...(isRight ? props.textStyle?.right : props.textStyle?.left),
            color: textColor,
          }}
          theme={theme}
          onReferencePress={handleReferencePress}
        />
      </View>
    );
  }, [theme, handleReferencePress]);

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 88 : 0}
      onLayout={handleLayout}
    >
      {/* Header showing context */}
      <View style={[styles.header, { backgroundColor: isWhatsAppContext ? '#25D366' : '#007AFF' }]}>
        <View style={styles.headerContent}>
          {isWhatsAppContext && (
            <MaterialCommunityIcons name="whatsapp" size={20} color="white" style={styles.headerIcon} />
          )}
          <Text color="white" fontSize="$4" fontWeight="600">
            {recipientName || recipientId}
          </Text>
        </View>
      </View>

      <GiftedChat
        messages={messages}
        onSend={onSend}
        user={currentUser}
        renderSend={renderSend}
        renderInputToolbar={renderInputToolbar}
        renderComposer={renderComposer}
        renderBubble={renderBubble}
        renderMessageText={renderMessageText}
        listProps={{
          contentContainerStyle: {
            paddingHorizontal: 8,
          },
        }}
        {...(messages.length > 0 && {
          loadEarlier: true,
          onLoadEarlier: loadEarlier,
          isLoadingEarlier: isLoadingEarlier,
        })}
        showUserAvatar={!isWhatsAppContext}
        showAvatarForEveryMessage={!isWhatsAppContext}
        renderAvatarOnTop={!isWhatsAppContext}
        dateFormat="DD/MM/YYYY"
        timeFormat="HH:mm"
        isTyping={isLoading}
        scrollToBottom
        scrollToBottomComponent={() => (
          <MaterialCommunityIcons 
            name="chevron-down" 
            size={24} 
            color={isWhatsAppContext ? "#25D366" : "#007AFF"} 
          />
        )}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F5F5F5',
  },
  header: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  headerIcon: {
    marginRight: 8,
  },
  sendButton: {
    marginRight: 10,
    marginBottom: 5,
    justifyContent: 'center',
    alignItems: 'center',
    width: 40,
    height: 40,
  },
  inputToolbar: {
    borderTopWidth: 1,
    paddingTop: 6,
  },
  accessory: {
    height: 44,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 8,
    paddingRight: 8,
  },
  composer: {
    borderWidth: 1,
    borderRadius: 20,
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 8,
    marginLeft: 8,
    marginRight: 8,
  },
});
